<script setup>
import { useAppStore } from '@/stores/App'
import { useDisplay } from 'vuetify'

// mobile breakpoint
const { xs, smAndDown } = useDisplay()

// Vars
const app = useAppStore()
const staticURLImg = useRuntimeConfig().public.staticURLImg
const lightBoxEarly = ref(null)
const lightBoxLatest = ref(null)

// SEO
useSeoMeta({
    title: 'About Us',
    ogTitle: 'About Us',
    description: 'About satYatunes',
    ogDescription: 'About satYatunes',
})
</script>

<template>
    <v-row id="about" class="d-flex flex-column align-center slide-from-top">
        <v-col v-if="!xs" cols="12" class="pa-0">
            <v-img src="/wbst-banner-02.webp" alt="satYatunes banner" cover></v-img>
        </v-col>
        <!-- <v-col cols="10" sm="10" class="mt-8 text-h3 text-center font-weight-medium st-text-shadow">
            The Journey
        </v-col> -->
        <v-col cols="10" class="mt-8 st-text-normal--justify">
            satYatunes began as a creative outlet for Satya Choudhury, a space to share the love for music and visual
            art. In 2010, this passion took shape with the release of our first graphic design project: a Reaper theme.
            This was followed by a series of themes and skins for popular audio software like Poise, Stereo Chorus, and
            Alchemy Player and Wusik 4000.
        </v-col>
        <v-col cols="9" class="my-16">
            <v-row id="earlydesignimageslider" justify="center" class="fade-in-up">
                <v-col class="rounded bg-grey-darken-4 st-box-shadow">
                    <v-row dense justify="center">
                        <v-col cols="12" class="mb-4 text-center">
                            <app-section-header
                                :id="`earlydesigns`"
                                :title="`Early Designs`"
                                :subtitle="`Some of our early designs. Click an image to open the gallery.`"
                            />
                        </v-col>
                        <v-col>
                            <v-sheet class="mb-12 d-flex justify-center bg-transparent">
                                <v-slide-group show-arrows prev-icon="mdi-chevron-left" next-icon="mdi-chevron-right">
                                    <v-slide-group-item v-for="(img, n) in app.earlyDesigns" :key="img.alt">
                                        <v-card
                                            variant="flat"
                                            class="ma-3 bg-transparent"
                                            width="200"
                                            @click.stop="lightBoxEarly.open(n)"
                                        >
                                            <v-img
                                                :src="staticURLImg + img.source"
                                                :alt="img.alt"
                                                contain
                                                class="rounded elevation-1"
                                            >
                                            </v-img>
                                        </v-card>
                                    </v-slide-group-item>
                                </v-slide-group>
                            </v-sheet>
                        </v-col>
                        <the-lightbox ref="lightBoxEarly" :data="app.earlyDesigns" />
                    </v-row>
                </v-col>
            </v-row>
        </v-col>
        <v-col cols="10" class="st-text-normal--justify">
            While initially focused on sound design and crafting demo tracks, our path began to shift. Our Alchemy
            Player skin garnered the attention of the Camel Audio team, signifying a talent for user interface (UI)
            design. This fueled a renewed focus on graphic design, culminating in the release of our highly successful
            <span class="font-weight-bold text-deep-purple">MayaBlue</span> skin for Melda plugins in 2015. The
            overwhelming positive response solidified our commitment to the world of GUI design.
        </v-col>
        <v-col cols="10" sm="10" class="st-text-normal--justify">
            We haven't abandoned our roots in sound design, we still take on projects that involve crafting sounds and
            demo tracks. However, our expertise in UI/UX design has truly blossomed. Over the years, we have designed
            and delivered over <span class="font-weight-bold text-deep-purple">70</span> GUI projects for audio
            applications and plugins, including more than
            <span class="font-weight-bold text-deep-purple">15</span> custom Kontakt GUIs. Our creative touch has also
            graced logos and album artwork.
        </v-col>
        <v-col cols="9" class="mt-16 bg-grey-darken-4 rounded st-box-shadow">
            <v-row dense>
                <v-col cols="12" class="mb-4 text-center">
                    <app-section-header
                        :id="`latestdesigns`"
                        :title="`Latest Designs`"
                        :subtitle="`Some of our latest designs. Click an image to open the gallery.`"
                    />
                </v-col>
                <template v-for="(img, n) in app.latestDesigns" :key="img.alt">
                    <v-col :cols="img.cols">
                        <v-img
                            :src="staticURLImg + img.source"
                            height="100%"
                            cover
                            @click.stop="lightBoxLatest.open(n)"
                            class="rounded elevation-1"
                            :alt="img.alt"
                        ></v-img>
                    </v-col>
                </template>
                <the-lightbox ref="lightBoxLatest" :data="app.latestDesigns" />
            </v-row>
        </v-col>
        <v-col cols="10" sm="10" class="mt-16 st-header"> A New Chapter: satYatunes Opens Its Doors </v-col>
        <v-col cols="10" sm="10" class="st-text-normal--justify">
            We're thrilled to announce that satYatunes is expanding its reach to embrace fellow designers and creators!
            This exciting new platform will be a dedicated space to showcase their talent in addition to our own
            creative projects.
        </v-col>
        <v-col cols="10" sm="10" class="st-text-normal--justify">
            We understand the challenges faced by designers. We started by sharing our work on forums and blogs, a
            common approach for many even today. However, maintaining a website or blog can be time-consuming, and it's
            easy for fantastic creations to get buried after a short while. We aim to solve this problem by providing a
            permanent and discoverable home for these hidden gems.
        </v-col>
        <v-col cols="10" sm="10" class="st-text-normal--justify">
            We're brimming with innovative ideas that we can't wait to bring to life in the coming years. Stay tuned as
            we transform into a vibrant hub for creativity!
        </v-col>
        <v-col cols="10" sm="10" class="mt-8 mb-16 st-text--justify">
            Feel free to check out the
            <nuxt-link to="/services" class="font-weight-medium" aria-label="Explore satYatunes services">
                Services
            </nuxt-link>
            we offer or
            <nuxt-link to="/support" class="font-weight-medium" aria-label="Contact us">Contact</nuxt-link> us if you
            have any queries.
        </v-col>
        <v-col cols="12" class="mb-16">
            <v-row class="pb-6 justify-center">
                <v-col v-for="logo in app.foliositeLogo" :key="logo.alt" cols="6" sm="4" lg="2" class="text-center">
                    <nuxt-link :to="logo.url" target="_blank" class="text-decoration-none">
                        <v-avatar tile :size="smAndDown ? 64 : 80">
                            <v-hover>
                                <template v-slot:default="{ isHovering, props }">
                                    <v-img
                                        v-bind="props"
                                        :src="isHovering ? staticURLImg + logo.sourceRgb : staticURLImg + logo.source"
                                        class="rounded"
                                        :alt="logo.alt"
                                    ></v-img>
                                </template>
                            </v-hover>
                        </v-avatar>
                        <div class="mt-2 font-weight-medium text-grey text-body-2">
                            {{ logo.alt }}
                        </div>
                    </nuxt-link>
                </v-col>
                <!-- <v-col cols="12" class="mt-4 text-center text-grey"> Visit any of these sites to learn more. </v-col> -->
            </v-row>
        </v-col>
    </v-row>
</template>

<style scoped></style>
